import { checkDeadlinePassed } from '../../../../special/checkDeadlinePassed';

// Phases and work packages both carry a taskStats object. The project level is
// the sum over the phases, the bars use the stats of the work packages.
// Overdue tasks are a subset of the open ones.
export function normalizeTaskStats(entry) {
  const stats = entry.taskStats || {};

  const total = stats.totalTasks || 0;
  const done = stats.doneTasks || 0;

  return {
    id: entry.id,
    title: entry.title || entry.name || '',
    icon: entry.icon,
    total,
    done,
    open: total - done,
    overdue: stats.overdueTasks || 0,
    totalScore: stats.totalEffortScore || 0,
    doneScore: stats.doneEffortScore || 0,
  };
}

export function sumTaskStats(list) {
  return list.reduce(
    (sum, item) => ({
      total: sum.total + item.total,
      done: sum.done + item.done,
      open: sum.open + item.open,
      overdue: sum.overdue + item.overdue,
      totalScore: sum.totalScore + item.totalScore,
      doneScore: sum.doneScore + item.doneScore,
    }),
    { total: 0, done: 0, open: 0, overdue: 0, totalScore: 0, doneScore: 0 },
  );
}

export function donePercent({ total, done }) {
  return total ? Math.round((done / total) * 100) : 0;
}

const COUNTERS = [
  'totalTasks',
  'doneTasks',
  'overdueTasks',
  'totalEffortScore',
  'doneEffortScore',
];

// what a single task adds to the counters of its work package
function taskWeight(task) {
  if (!task) return {};

  const isDone = Boolean(task.status);
  const score = Number(task.effortScore || 0);

  return {
    totalTasks: 1,
    doneTasks: isDone ? 1 : 0,
    overdueTasks:
      !isDone && task.deadline && checkDeadlinePassed(task.deadline) ? 1 : 0,
    totalEffortScore: score,
    doneEffortScore: isDone ? score : 0,
  };
}

/**
 * Difference one changed task makes. `before` is null for a new task,
 * `after` is null for a deleted one.
 */
export function taskStatsDelta(before, after) {
  const from = taskWeight(before);
  const to = taskWeight(after);

  return COUNTERS.reduce(
    (delta, key) => ({ ...delta, [key]: (to[key] || 0) - (from[key] || 0) }),
    {},
  );
}

const addDelta = (stats, delta) =>
  COUNTERS.reduce(
    (next, key) => ({ ...next, [key]: (stats?.[key] || 0) + delta[key] }),
    { ...stats },
  );

/**
 * Writes the delta into the changed work package and its phase, so the overview
 * stays correct without loading the phases again.
 */
export function applyTaskStatsDelta(phases, workPackageId, delta) {
  return phases.map((phase) => {
    const workPackages = phase.workPackages || [];
    if (!workPackages.some((wp) => wp.id === workPackageId)) return phase;

    return {
      ...phase,
      taskStats: addDelta(phase.taskStats, delta),
      workPackages: workPackages.map((wp) =>
        wp.id === workPackageId
          ? { ...wp, taskStats: addDelta(wp.taskStats, delta) }
          : wp,
      ),
    };
  });
}
